import { FavoriteService } from '@services/api/favorite.service';
import { ToastService } from '@services/utils/toast.service';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export interface IFavorite {
  id: string;
  name: string;
  icon?: string;
  path: string;
  sl?: number;
}

interface IFavoritesContext {
  favorites: IFavorite[];
  loading: boolean;
  refresh: () => void;
  isFavorite: (path: string) => boolean;
  addFavorite: (name: string, path: string, icon?: string) => Promise<void>;
  removeFavorite: (id: string) => Promise<void>;
  removeByPath: (path: string) => Promise<void>;
}

const FavoritesContext = createContext<IFavoritesContext>({} as IFavoritesContext);

const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const [favorites, setFavorites] = useState<IFavorite[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const refresh = () => {
    setLoading(true);
    FavoriteService.list()
      .then((res) => setFavorites(res?.body || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const isFavorite = (path: string) => favorites.some((f) => f.path === path);

  const addFavorite = async (name: string, path: string, icon = 'star') => {
    try {
      const res = await FavoriteService.add({ name, path, icon });
      setFavorites(res?.body || []);
      ToastService.success('Added to favorites');
    } catch (err: any) {
      ToastService.error(err?.msg || 'Unable to add favorite');
    }
  };

  const removeFavorite = async (id: string) => {
    try {
      await FavoriteService.remove(id);
      setFavorites((prev) => prev.filter((f) => f.id !== id));
      ToastService.success('Removed from favorites');
    } catch (err: any) {
      ToastService.error(err?.msg || 'Unable to remove favorite');
    }
  };

  const removeByPath = async (path: string) => {
    const fav = favorites.find((f) => f.path === path);
    if (fav) await removeFavorite(fav.id);
  };

  return (
    <FavoritesContext.Provider
      value={{ favorites, loading, refresh, isFavorite, addFavorite, removeFavorite, removeByPath }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

const useFavorites = () => useContext(FavoritesContext);

export { FavoritesProvider, useFavorites };
